import { hasSplashback, hasWorktop } from '@bulthaup/api/entities/project';
import { Button, Group, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconFileInvoice } from '@tabler/icons-react';

import { RouterOutput, trpc } from '../../../../../services/trpc';
import { getUTDocumentUrl } from '../../../../../services/upload';

type GenerateInvoiceButtonProps = {
  customerId: string;
  project: NonNullable<RouterOutput['projects']['get']>;
};

function getMissingRequirements(
  project: NonNullable<RouterOutput['projects']['get']>,
) {
  const missingRequirements: string[] = [];

  if (!project.isBulthaupOrderSent) {
    missingRequirements.push('la commande bulthaup doit être envoyée');
  }

  if (!project.isApplianceOrderSent) {
    missingRequirements.push('la commande électro doit être envoyée');
  }

  const isCompliant =
    project.isTechnicalPlanVerifiedOnSite &&
    project.isConstructionSiteCompliant;
  if (!isCompliant) {
    const message = 'le chantier doit être conforme au plan technique';
    missingRequirements.push(message);
  }

  if (!project.isInstallationFolderReady) {
    missingRequirements.push('le dossier poseur doit être prêt');
  }

  if (hasWorktop(project) && !project.isWorktopProviderNotified) {
    const message = 'le fournisseur plan de travail doit être notifié';
    missingRequirements.push(message);
  }

  if (hasSplashback(project) && !project.isSplashbackProviderNotified) {
    const message = 'le fournisseur de crédence doit être notifié';
    missingRequirements.push(message);
  }

  if (!project.isCustomerNotifiedOfInstallationDate) {
    const message =
      "le client et l'archi doivent être informés de la date de pose";
    missingRequirements.push(message);
  }

  if (!project.deliveryDate) {
    missingRequirements.push('la date de livraison doit être documentée');
  }

  return missingRequirements;
}

export function GenerateInvoiceButton({
  customerId,
  project,
}: GenerateInvoiceButtonProps) {
  const generateInvoiceMutation = trpc.documents.generateInvoice.useMutation();
  const utils = trpc.useUtils();
  const { data: customer } = trpc.projects.getCustomer.useQuery({
    id: customerId,
  });
  const finalInvoiceDocument = (
    customer as
      | (typeof customer & { finalInvoiceDocument?: { utFileKey: string } })
      | undefined
  )?.finalInvoiceDocument;
  const hasFinalInvoice = !!finalInvoiceDocument;
  const missingRequirements = getMissingRequirements(project);
  const isFinalInvoiceBlocked = missingRequirements.length > 0;
  const finalInvoiceTooltipLabel = `Conditions requises avant de valider la facture définitivement :\n${missingRequirements.map(requirement => `- ${requirement}`).join('\n')}`;

  const handleGenerateFinalInvoice = () => {
    if (missingRequirements.length > 0) {
      notifications.show({
        title: 'Conditions requises avant de valider la facture définitivement :',
        message: (
          <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
            {`- ${missingRequirements.join('\n- ')}`}
          </Text>
        ),
        color: 'orange',
      });
      return;
    }

    void generateInvoice(false);
  };

  const runGenerateInvoice = async (provisional: boolean) => {
    try {
      const result = await generateInvoiceMutation.mutateAsync({
        customerId,
        provisional,
      });

      if (!provisional) {
        utils.projects.getCustomer.invalidate({ id: customerId });
      }

      const url = URL.createObjectURL(
        new Blob([result.buffer], { type: 'application/pdf' }),
      );
      window.open(url, '_blank');
      notifications.show({
        title: provisional ? 'Facture provisoire affichée' : 'Facture affichée',
        message: 'La facture a été ouverte dans un nouvel onglet',
        color: 'green',
      });
    } catch (error) {
      console.error('Error generating invoice:', error);
      notifications.show({
        title: 'Erreur',
        message:
          error instanceof Error
            ? error.message
            : 'Une erreur est survenue lors de la génération de la facture',
        color: 'red',
      });
    }
  };

  const generateInvoice = async (provisional: boolean) => {
    if (provisional) {
      await runGenerateInvoice(true);
      return;
    }

    modals.openConfirmModal({
      title: hasFinalInvoice
        ? 'Régénérer la facture définitive'
        : 'Générer la facture définitive',
      children: (
        <Text size="sm">
          {hasFinalInvoice
            ? 'Voulez-vous régénérer la facture définitive avec les paiements à jour ?'
            : 'Voulez-vous vraiment générer une facture définitive ?'}
        </Text>
      ),
      labels: {
        confirm: hasFinalInvoice ? 'Régénérer' : 'Générer',
        cancel: 'Annuler',
      },
      onConfirm: () => {
        void runGenerateInvoice(false);
      },
    });
  };

  return (
    <Group justify="flex-end" wrap="wrap">
      {hasFinalInvoice && finalInvoiceDocument ? (
        <>
          <Button
            leftSection={<IconFileInvoice size={16} />}
            onClick={() => {
              const doc = finalInvoiceDocument;
              if (doc) window.open(getUTDocumentUrl(doc.utFileKey), '_blank');
            }}
            variant="outline"
          >
            Afficher la facture
          </Button>
          <Button
            leftSection={<IconFileInvoice size={16} />}
            onClick={() => generateInvoice(false)}
            loading={generateInvoiceMutation.isPending}
          >
            Régénérer la facture
          </Button>
        </>
      ) : (
        <>
          <Button
            leftSection={<IconFileInvoice size={16} />}
            onClick={() => generateInvoice(true)}
            loading={generateInvoiceMutation.isPending}
            variant="outline"
          >
            Prévisualiser la facture
          </Button>
          {isFinalInvoiceBlocked ? (
            <Tooltip
              multiline
              label={
                <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                  {finalInvoiceTooltipLabel}
                </Text>
              }
            >
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={handleGenerateFinalInvoice}
                loading={generateInvoiceMutation.isPending}
                aria-disabled
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
              >
                Valider la facture définitivement
              </Button>
            </Tooltip>
          ) : (
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleGenerateFinalInvoice}
              loading={generateInvoiceMutation.isPending}
            >
              Valider la facture définitivement
            </Button>
          )}
        </>
      )}
    </Group>
  );
}
